/**
 * GT custom: styling for the Shopify Forms app that its CSS custom properties
 * cannot reach.
 *
 * Prefer assets/custom.css for anything the app exposes as a variable on :host —
 * that needs no JavaScript, because the CSS cascade compares *context* before
 * specificity and a normal declaration in the outer tree beats the inner one.
 * See the comment above the `shopify-forms-embed` block in custom.css.
 *
 * This file exists only for what the app does NOT expose. Right now that is the
 * radio controls: there is no --radio-* variable of any kind, so the only way in
 * is to adopt a stylesheet into the shadow root.
 *
 * Two rules for anything added here:
 *
 *   1. Never select on the app's class names. They look like _radioInput_nw6va_31
 *      — CSS-module build hashes, regenerated whenever Shopify ships a new build
 *      of the app. Styling keyed to them dies silently on someone else's release
 *      schedule. Use element, attribute and state selectors, which are stable.
 *      If a structural hook is unavoidable, the data-testid attributes
 *      (form, app-router, btn-form-submit, field-email, ...) are the safer bet.
 *
 *   2. Keep it small. Every rule here is a rule that has to be re-verified after
 *      an app update.
 */
(() => {
  const CSS = `
    /* Radio controls. The app draws these itself and exposes no variable for them,
       so take the rendering over completely with appearance:none rather than trying
       to patch whatever it does — that way this does not depend on the app's own
       rules, only on overriding them.

       !important throughout: this sheet lands in the same tree context as the app's
       stylesheet, so context no longer decides the cascade and it comes down to
       specificity and order. Ours is adopted last and these selectors outrank a
       single class, but the app's rules are not visible from here, so this removes
       the guesswork. */
    input[type="radio"] {
      /* The two numbers worth touching.

         --gt-radio-size: overall diameter. Started at 32px (double the ~16px native
         default), then took a third back off. Whole pixels keep the ring crisp —
         21.33px would land the 1px outer shadow on a subpixel boundary.

         --gt-radio-dot: diameter of the checked centre dot, as a fraction of the
         control. Raise toward 1 for a fatter dot and a thinner white gap; the ring
         stays put either way, since it is a shadow rather than part of the box.
         At 0.72 the gap works out at ~2.9px. That is deliberate, but it does mean
         the two variables are no longer independent: drop --gt-radio-size much below
         21px and the gap starts disappearing, so lower the dot fraction alongside it. */
      --gt-radio-size: 21px;
      --gt-radio-dot: 0.72;

      appearance: none !important;
      -webkit-appearance: none !important;
      box-sizing: border-box !important;

      /* flex:none and align-self guard against the group's row layout squashing
         or stretching the control now that it is considerably bigger. */
      flex: none !important;
      align-self: center !important;

      width: var(--gt-radio-size) !important;
      height: var(--gt-radio-size) !important;

      /* Spacing is NOT set here — see the label rule below for why. Zeroed so that
         if a future build of the app drops its own margin, the gap does not double. */
      margin: 0 !important;

      border: 0 !important;
      border-radius: 50% !important;
      background: #fff !important;

      /* Outer ring drawn as a spread shadow rather than a border, so the border
         property stays free to draw the inner gap when checked (below), and so the
         ring can change weight without the control changing size. This mirrors how
         the app itself outlines text fields — see --form-field-border in custom.css. */
      box-shadow: 0 0 0 1px #000 !important;

      vertical-align: middle !important;
      cursor: pointer !important;
      transition: background-color 120ms ease, border-width 120ms ease !important;
    }

    /* Checked: an opaque white border eats into the black background from the
       outside, leaving a black centre dot. box-sizing is border-box, so the
       control does not grow when the border appears.

       The border is the white gap, so its width is half of whatever the dot does not
       occupy — hence (1 - dot) / 2. Driving it from a fraction rather than hard-coding
       a border width means the proportion holds if --gt-radio-size changes. */
    input[type="radio"]:checked {
      border: calc(var(--gt-radio-size) * (1 - var(--gt-radio-dot)) / 2) solid #fff !important;
      background: #000 !important;
    }

    /* Gap between the control and its text.
       This was originally a margin on the input itself and had no visible effect:
       the app's own rule for it pairs two class names (0,2,0), which outranks an
       element+attribute selector (0,1,1), and with !important on both sides the
       cascade falls through to specificity. Setting it on the label sidesteps that
       contest entirely — the label owns the layout, so the gap belongs to it anyway.

       :has() keeps this off every other label in the form (the floating field labels
       and the fieldset legend are separate elements) by matching only a label that
       directly wraps a radio. Its specificity is that of its most specific argument,
       so this lands at 0,1,2 and clears the app's single-class rules.

       display:flex is asserted because gap needs a flex or grid container and the
       app's layout for this label cannot be seen from outside the shadow root. If it
       is already flex, this is a no-op.

       (No backticks anywhere in this string — the whole sheet is a template literal.) */
    label:has(> input[type="radio"]) {
      display: flex !important;
      align-items: center !important;
      gap: 15px !important;
    }

    input[type="radio"]:focus-visible {
      outline: none !important;
      box-shadow: 0 0 0 1px #000, 0 0 0 4px #e6510033 !important;
    }

    @media (prefers-reduced-motion: reduce) {
      input[type="radio"] { transition: none !important; }
    }

    /* Phone field's country-code button — capped height.
       Left to itself it stretches to match the phone input beside it, since the row
       is a flex container and flex items stretch by default.

       .phone-country-selector is one of the very few unhashed class names the app
       emits, so it is safe to select on, unlike its _selectToggle_12thd_25 child.
       [aria-expanded] is carried for specificity rather than filtering — it lifts this
       to 0,2,1, which clears a two-class app rule. That was learnt the hard way with
       the radio margin above.

       box-sizing so the cap counts the app's padding and border, and 50px means 50px
       rendered rather than 50px of content box. */
    .phone-country-selector button[aria-expanded] {
      box-sizing: border-box !important;
      max-height: 50px !important;
    }

    /* Gap between fields, 20px.

       The app exposes no variable for this. --spacing-size sounds like it and is not:
       its rendered :host block spends it entirely on --app-embed-padding-x/y, the
       padding around the whole embed. So the gap has to be set here.

       The form's direct children are the fields, in order: the shop-lead-capture
       wrapper, name, email, the phone row, the radio fieldset, the textarea, and the
       submit button. form[data-testid="form"] and the universal selector are the only
       hooks used — every class on those children is a build hash
       (_formFieldContainer_1mxsl_5 and friends) and would not survive an app update.

       Three rules, because the app's own spacing mechanism is not visible from out
       here and this has to land on exactly 20px either way:

         row-gap: 0        neutralises a gap if the form is a flex or grid column;
                           inert if it is a plain block.
         margin-block-end  zeroed on every child so a block-layout margin cannot
                           collapse against ours and win with a larger value.
         margin-block-start on each child after the first, which is then the only
                           thing producing space, under either layout.

       Doing it with margins rather than by forcing display:flex means the app's own
       layout is left alone — nothing here assumes what the form is.

       rem for the same reason as the button padding below: theme.liquid takes the root
       font size from 16px to 14px under 750px, so this rides down to 17.5px on mobile
       in step with the theme's contact form, which is set to the same 1.25rem.

       Worth checking on the rendered form: the first child wraps <shop-lead-capture>,
       which renders nothing on this page. If it has no height it still takes a slot,
       so the 20px above the name field may read as dead space at the top of the form.
       If it does, give that wrapper display:contents and it drops out of the layout
       without disturbing the custom properties it passes down. */
    form[data-testid="form"] {
      row-gap: 0 !important;
    }

    form[data-testid="form"] > * {
      margin-block-end: 0 !important;
    }

    form[data-testid="form"] > * + * {
      margin-block-start: 1.25rem !important;
    }

    /* Submit button — centred horizontally.
       Two properties because the form's own display value is not visible from outside
       the shadow root: align-self centres it if the form is flex or grid, auto inline
       margins centre it if the form is a plain block. Whichever does not apply is
       inert, so both is cheaper than guessing.

       data-testid is the app's own stable hook (unlike _formSubmitButton_jnbzb_106,
       which is a build hash); type="submit" rides along to reach 0,2,1 on specificity.

       Note this centres the button at whatever width it already has. If it turns out to
       be full-width, centring is a no-op by definition and it needs a width first — see
       the commented-out line. */
    button[type="submit"][data-testid="btn-form-submit"] {
      align-self: center !important;
      margin-inline: auto !important;
      /* width: auto !important; */

      /* Space above, separating the button from the last field. margin-block-start
         rather than margin-top so it stays paired with the logical margin-inline
         above. This now stands alone rather than stacking: the field-spacing rules
         above zero the form's row-gap, and this selector's (0,2,1) outranks their
         (0,1,1), so the button keeps 1.5rem while the fields sit at 1.25rem. */
      margin-block-start: 1.5rem !important;

      /* Horizontal padding only, so the button keeps its existing height. The app's
         --button-padding variable is a single value driving all four sides, so widening
         through that would have made the button taller too — hence a rule here rather
         than one line in custom.css.

         rem, not px: theme.liquid drops the root font size from 16px to 14px under
         750px, so 2.5rem rides that down from 40px to 35px on mobile without needing a
         second breakpoint here. rem stays root-relative inside a shadow root, so the
         shadow boundary makes no difference. */
      padding-inline: 2.5rem !important;
    }
  `;

  // One CSSStyleSheet object, adopted by every form on the page. Constructed
  // lazily so a page with no form builds nothing.
  let sheet = null;
  const getSheet = () => {
    if (!sheet) {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
    }
    return sheet;
  };

  // Keyed on the shadow root, not the host: adopting twice would be harmless but
  // pointless, and a WeakSet keeps no strong reference to removed forms.
  const adopted = new WeakSet();

  const applyTo = (host) => {
    const root = host.shadowRoot;
    // The element can exist before its shadow root does. Returning without
    // recording it means the next pass retries.
    if (!root || adopted.has(root)) return;
    adopted.add(root);
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, getSheet()];
  };

  const scan = (node) => {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.tagName === "SHOPIFY-FORMS-EMBED") applyTo(node);
    node.querySelectorAll?.("shopify-forms-embed").forEach(applyTo);
  };

  const scanAll = () => scan(document.documentElement);

  scanAll();

  // Forms arrive by two different routes and neither one alone is enough.
  //
  // A MutationObserver catches the element being inserted — the inline block on
  // page.repair is server-rendered with a declarative shadow root, but popup and
  // teaser layouts are injected later by the app.
  //
  // It does NOT catch attachShadow(), which fires no mutation record in the light
  // DOM. So an element seen with a null shadowRoot needs a second look, hence the
  // bounded poll. It stops as soon as a form is styled, and gives up after ~15s
  // rather than running for the life of the page.
  new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach(scan);
  }).observe(document.documentElement, { childList: true, subtree: true });

  let attempts = 0;
  const poll = setInterval(() => {
    scanAll();
    const found = document.querySelector("shopify-forms-embed")?.shadowRoot;
    if (found || (attempts += 1) > 75) clearInterval(poll);
  }, 200);
})();
